import React, { useState, useEffect } from 'react';
import {
    Table, Button, Modal, Form, Select, DatePicker, InputNumber, Row, Col, Tag, Typography, message, Checkbox, Tooltip, Popconfirm, Switch, Divider
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, AppstoreOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import DealServ from '../../service/DealServ';
import ProductServ from '../../service/ProductServ';
import Notify from '../../util/Notify';
import GlobalButton from '../../util/GlobalButton';
import { getImageUrl } from '../../component/SafeImage';
import { formatUnit } from '../../util/unitFormatter';
import DataTable from '../../component/ui/DataTable';
import Breadcrumbs from '../../component/Breadcrumbs';
import EmptyState from '../../component/EmptyState';
import '../../css/shops.css';

const { Option } = Select;
const { RangePicker } = DatePicker;

const DealsManager = () => {
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingDeal, setEditingDeal] = useState(null);
    const [form] = Form.useForm();
    const [products, setProducts] = useState([]);
    const [searchingProducts, setSearchingProducts] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [detailsVisible, setDetailsVisible] = useState(false);
    const [selectedDeal, setSelectedDeal] = useState(null);

    useEffect(() => {
        fetchDeals();
        fetchSellerProducts();
    }, []);

    const fetchDeals = async () => {
        setLoading(true);
        try {
            // Fetch deals for the logged-in seller
            const res = await DealServ.getMyDeals({ type: 'today' });
            const data = Array.isArray(res) ? res : (res.data || []);
            setDeals(data);
        } catch (error) {
            console.error("Error fetching deals:", error);
            Notify.error("Failed to load deals");
        } finally {
            setLoading(false);
        }
    };

    const fetchSellerProducts = async (search = "") => {
        setSearchingProducts(true);
        try {
            const res = await ProductServ.getSellerProducts({ search, limit: 100 });
            const data = Array.isArray(res) ? res : (res.data || []);
            setProducts(data);
        } catch (error) {
            console.error("Error fetching products:", error);
        } finally {
            setSearchingProducts(false);
        }
    };

    const handleEdit = (deal) => {
        setEditingDeal(deal);
        form.setFieldsValue({
            productId: deal.product?._id || deal.product,
            dateRange: [dayjs(deal.startDate), dayjs(deal.endDate)],
            optionName: deal.optionName,
            applicableVariants: deal.applicableVariants || (deal.variantValue ? [deal.variantValue] : []),
            discountPercentage: deal.discountPercentage,
            dealPrice: deal.dealPrice,
            isActive: deal.isActive
        });
        setModalOpen(true);
    };

    const handleDelete = async (record) => {
        const idToDelete = record._id || record.id;

        if (!idToDelete) {
            Notify.error("Invalid deal ID");
            return;
        }

        try {
            await DealServ.remove(idToDelete);
            message.success("Deal removed successfully");
            fetchDeals();
        } catch (error) {
            console.error("Error deleting deal:", error);
            Notify.error(error.message || "Failed to remove deal");
        }
    };

    const toggleLive = async (record) => {
        try {
            const newStatus = !record.isLive;
            await DealServ.update(record._id, { isLive: newStatus });
            message.success(`Deal is now ${newStatus ? 'Active' : 'Inactive'}`);

            // Update selectedDeal state if modal is open to reflect change in Switch
            if (selectedDeal && (selectedDeal._id === record._id || selectedDeal.id === record._id)) {
                setSelectedDeal({ ...selectedDeal, isLive: newStatus });
            }

            fetchDeals();
        } catch (error) {
            console.error("Error updating live status:", error);
            message.error("Failed to update live status");
        }
    };

    const handleModalClose = () => {
        setModalOpen(false);
        setEditingDeal(null);
        form.resetFields();
    };

    const onFinish = async (values) => {
        if (!values.dateRange || values.dateRange.length !== 2) {
            Notify.error("Please select a valid date range");
            return;
        }

        const [start, end] = values.dateRange;
        if (end.isBefore(start)) {
            Notify.error("End date cannot be before start date");
            return;
        }

        try {
            setSubmitting(true);
            const selectedProduct = products.find(p => p._id === values.productId || p.id === values.productId);

            let finalDealPrice = values.dealPrice;
            let finalDiscount = values.discountPercentage;

            if (selectedProduct && (values.dealPrice === undefined || values.discountPercentage === undefined)) {
                const originalPrice = selectedProduct.mrp || selectedProduct.price;
                if (values.dealPrice && !values.discountPercentage) {
                    finalDiscount = Math.round(((originalPrice - values.dealPrice) / originalPrice) * 100);
                } else if (values.discountPercentage && !values.dealPrice) {
                    finalDealPrice = Math.round(originalPrice * (1 - values.discountPercentage / 100));
                }
            }

            const payload = {
                product: values.productId,
                optionName: values.optionName,
                applicableVariants: values.applicableVariants || [],
                type: 'today', // Always today
                startDate: start.toISOString(),
                endDate: end.toISOString(),
                discountPercentage: finalDiscount,
                dealPrice: finalDealPrice,
                isActive: true,
                isLive: editingDeal ? editingDeal.isLive : true
            };

            if (editingDeal) {
                await DealServ.update(editingDeal._id, payload);
                Notify.success("Deal updated successfully");
            } else {
                await DealServ.create(payload);
                Notify.success("Deal created successfully");
            }

            fetchDeals();
            handleModalClose();
        } catch (error) {
            console.error("Error saving deal:", error);
            Notify.error(error, "Failed to save deal");
        } finally {
            setSubmitting(false);
        }
    };

    const columns = [
        {
            title: 'Product',
            dataIndex: 'product',
            key: 'product',
            render: (product, record) => {
                let variants = record.applicableVariants || [];
                if (variants.length === 0 && record.variantValue) variants = [record.variantValue];
                if (variants.length === 0 && product?.options?.length > 0) {
                    const v = product.options[0]?.variants?.[0];
                    if (v) variants = [v.value];
                }

                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {product?.imageUrl && (
                            <img
                                src={product.imageUrl}
                                alt=""
                                style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 8, border: '1px solid #f0f0f0', flexShrink: 0 }}
                            />
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span style={{ fontWeight: 600, fontSize: 13, color: '#1a1a2e', lineHeight: 1.3 }}>
                                {product?.name || 'Unknown Product'}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: 11, color: '#888' }}>MRP: ₹{product?.mrp}</span>
                                {record.optionName && (
                                    <span style={{ fontSize: 10, background: '#e8f4fd', color: '#1890ff', padding: '1px 6px', borderRadius: 4, fontWeight: 500 }}>
                                        {record.optionName}
                                    </span>
                                )}
                            </div>
                            {variants.length > 0 && (
                                <span style={{ fontSize: 11, color: '#52c41a', fontWeight: 500 }}>
                                    {variants.map(v => formatUnit(v)).join(', ')}
                                </span>
                            )}
                        </div>
                    </div>
                );
            }
        },
        {
            title: 'Discount',
            key: 'discount',
            width: 120,
            align: 'center',
            render: (_, record) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ color: '#52c41a', fontWeight: 700, fontSize: 13 }}>{record.discountPercentage}% OFF</span>
                    <span style={{ color: '#FF8800', fontWeight: 600, fontSize: 13 }}>₹{record.dealPrice}</span>
                </div>
            )
        },
        {
            title: 'Live Status',
            key: 'status',
            width: 100,
            align: 'center',
            render: (_, record) => (
                <Tag color={record.isLive ? "success" : "error"} style={{ margin: 0, borderRadius: 4, fontWeight: 600 }}>
                    {record.isLive ? "Active" : "Inactive"}
                </Tag>
            )
        },
        {
            title: 'Duration',
            key: 'duration',
            width: 160,
            align: 'center',
            render: (_, record) => (
                <div style={{ fontSize: 12, color: '#555', lineHeight: 1.6, textAlign: 'left', display: 'inline-block' }}>
                    <div><span style={{ color: '#888', marginRight: 4 }}>Start:</span>{dayjs(record.startDate).format('MMM D, HH:mm')}</div>
                    <div><span style={{ color: '#888', marginRight: 4 }}>End:</span>{dayjs(record.endDate).format('MMM D, HH:mm')}</div>
                </div>
            )
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            width: 100,
            render: (_, record) => (
                <Button
                    type="primary"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedDeal(record);
                        setDetailsVisible(true);
                    }}
                    style={{
                        borderRadius: 3,
                        fontWeight: 600,
                        background: '#ffffffff',
                        color: '#004754ff',
                        border: '1px solid #e4e4e7',
                        boxShadow: '0 0 0 1px #e4e4e7',
                        height: 'auto',
                        padding: '0px 8px',
                        fontSize: 13,
                    }}
                >
                    View
                </Button>
            )
        }

    ];

    return (
        <div className="ss-wrapper">
            <div className="ss-container">

                {/* Header Section */}
                <div className="ss-header">
                    <div className="ss-header-left">
                        <div className="ss-header-icon">
                            <AppstoreOutlined style={{ fontSize: 22, color: '#4776E6' }} />
                        </div>
                        <div>
                            <h1 className="ss-title">Today's Deals Manager</h1>
                            <p className="ss-subtitle">{deals.length} deals listed</p>
                        </div>
                    </div>
                    <div>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setModalOpen(true)}
                            style={{ height: 42, borderRadius: 8, fontWeight: 600, padding: '0 24px', background: 'linear-gradient(135deg, #FF8800 0%, #E67E22 100%)', border: 'none', boxShadow: '0 4px 12px rgba(255, 136, 0, 0.2)' }}
                        >
                            Create Today Deal
                        </Button>
                    </div>
                </div>

                {/* Table Section */}
                <div className="ss-table-wrap">
                    {deals.length > 0 || loading ? (
                        <Table
                            className="custom-table table-container"
                            columns={columns}
                            dataSource={deals}
                            rowKey={(record) => record._id || record.id}
                            loading={loading}
                            pagination={{
                                className: "custom-pagination pcu-padding-16",
                                pageSize: 10,
                                onChange: (page) => {
                                    const pageContent = document.querySelector('.page-content');
                                    if (pageContent) pageContent.scrollTo({ top: 0, behavior: 'smooth' });
                                    else window.scrollTo({ top: 0, behavior: 'smooth' });
                                }
                            }}
                            bordered={false}
                            scroll={{ x: 'max-content' }}
                        />
                    ) : (
                        <EmptyState
                            title="No Deals Found"
                            description="You haven't created any deals for today."
                            icon="tag"
                            gradient="orange"
                            onAction={() => setModalOpen(true)}
                            actionText="Create Today Deal"
                            showAction={true}
                        />
                    )}
                </div>
            </div>

            <Modal
                title={editingDeal ? "Edit Today Deal" : "Create Today Deal"}
                open={modalOpen}
                onCancel={handleModalClose}
                footer={null}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    onValuesChange={(changedValues, allValues) => {
                        const selectedProdId = allValues.productId;
                        const product = products.find(p => p._id === selectedProdId || p.id === selectedProdId);

                        if (product) {
                            // Helper to get all variants
                            let allVariants = [];
                            if (product.options && product.options.length > 0) {
                                product.options.forEach(opt => {
                                    if (opt.variants) allVariants.push(...opt.variants);
                                });
                            } else if (product.variants) {
                                allVariants = product.variants;
                            }

                            // 1. Reset and auto-select logic if product changed
                            if (changedValues.productId) {
                                form.setFieldsValue({
                                    optionName: undefined,
                                    applicableVariants: [],
                                    discountPercentage: undefined,
                                    dealPrice: undefined
                                });
                                // Auto-select if ONLY ONE option exists
                                if (product.options && product.options.length === 1) {
                                    const opt = product.options[0];
                                    form.setFieldsValue({ optionName: opt.name });
                                    const allVals = opt.variants.map(v => v.value);
                                    form.setFieldsValue({ applicableVariants: allVals });
                                } else if (!product.options || product.options.length === 0) {
                                    // Handle legacy variants
                                    if (product.variants && product.variants.length > 0) {
                                        const allVals = product.variants.map(v => v.value);
                                        form.setFieldsValue({ applicableVariants: allVals });
                                    }
                                }
                                return;
                            }

                            // Reset variants if optionName changed
                            if (changedValues.optionName) {
                                form.setFieldsValue({ applicableVariants: [] });
                                return;
                            }

                            // 2. Extract variants based on selected optionName
                            let targetedVariants = [];
                            if (allValues.optionName) {
                                const opt = product.options.find(o => o.name === allValues.optionName);
                                if (opt) targetedVariants = opt.variants;
                            } else if (!product.options || product.options.length === 0) {
                                targetedVariants = product.variants || [];
                            }

                            // 3. Calculation logic
                            const firstSelectedValue = allValues.applicableVariants?.[0];
                            const firstVariant = targetedVariants.find(v => v.value === firstSelectedValue);
                            let mrp = firstVariant?.mrp || product.mrp || product.price;

                            if (changedValues.discountPercentage !== undefined || (changedValues.applicableVariants && allValues.discountPercentage)) {
                                const targetDiscount = changedValues.discountPercentage || allValues.discountPercentage;
                                if (mrp && targetDiscount) {
                                    const newPrice = Math.round(mrp * (1 - targetDiscount / 100));
                                    form.setFieldsValue({ dealPrice: newPrice });
                                }
                            } else if (changedValues.dealPrice !== undefined) {
                                if (mrp && (changedValues.dealPrice || changedValues.dealPrice === 0)) {
                                    const newDiscount = Math.round(((mrp - changedValues.dealPrice) / mrp) * 100);
                                    form.setFieldsValue({ discountPercentage: newDiscount });
                                }
                            }
                        }
                    }}
                >
                    <Form.Item
                        name="productId"
                        label="Select Product"
                        rules={[{ required: true, message: 'Please select a product' }]}
                    >
                        <Select
                            showSearch
                            placeholder="Search product by name"
                            onSearch={fetchSellerProducts}
                            loading={searchingProducts}
                            optionLabelProp="label"
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            disabled={!!editingDeal}
                            onChange={() => form.setFieldsValue({ variantValue: undefined, discountPercentage: undefined, dealPrice: undefined })}
                        >
                            {products.map(p => {
                                // Extract quantity info from variants or name if possible
                                let quantityLabel = "";
                                if (p.variants && p.variants.length > 0) {
                                    quantityLabel = p.variants[0].value;
                                } else if (p.options && p.options.length > 0) {
                                    const variant = p.options[0]?.variants?.[0];
                                    if (variant) quantityLabel = variant.value;
                                }

                                return (
                                    <Option key={p._id || p.id} value={p._id || p.id} label={p.name}>
                                        <div className="pcu-flex pcu-items-center pcu-gap-12 pcu-padding-y-4">
                                            <img
                                                src={getImageUrl(p.imageUrl)}
                                                alt=""
                                                className="pcu-w-44px pcu-h-44px pcu-object-cover pcu-radius-6 pcu-border-slate-light"
                                                onError={(e) => { e.target.src = 'https://via.placeholder.com/44?text=No+Img'; }}
                                            />
                                            <div className="pcu-flex pcu-flex-column pcu-line-h-1.3">
                                                <span className="pcu-font-600 pcu-font-14">{p.name}</span>
                                                <div className="pcu-flex pcu-items-center pcu-gap-8">
                                                    <span className="pcu-font-12 pcu-color-error">MRP: ₹{p.mrp}</span>
                                                    {quantityLabel && (
                                                        <Tag color="green" className="pcu-font-10 pcu-h-18 pcu-line-h-16">
                                                            {formatUnit(quantityLabel)}
                                                        </Tag>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </Option>
                                );
                            })}
                        </Select>
                    </Form.Item>

                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.productId !== curr.productId || prev.optionName !== curr.optionName}>
                        {({ getFieldValue }) => {
                            const pid = getFieldValue('productId');
                            const product = products.find(p => p._id === pid || p.id === pid);
                            if (!product) return null;

                            const optionName = getFieldValue('optionName');
                            const hasModernOptions = product.options && product.options.length > 0;

                            let targetedVariants = [];
                            let showOptionSelector = false;

                            if (hasModernOptions) {
                                showOptionSelector = true;
                                if (optionName) {
                                    const opt = product.options.find(o => o.name === optionName);
                                    if (opt) targetedVariants = opt.variants;
                                }
                            } else {
                                targetedVariants = product.variants || [];
                            }

                            return (
                                <>
                                    {showOptionSelector && (
                                        <Form.Item
                                            name="optionName"
                                            label="Select Variant Type (e.g. Kilograms, Liters, Pieces)"
                                            rules={[{ required: true, message: 'Please select an option type' }]}
                                        >
                                            <Select placeholder="Choose dimension">
                                                {product.options.map(o => (
                                                    <Option key={o.name} value={o.name}>{o.name}</Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    )}

                                    {(targetedVariants.length > 0) ? (
                                        <Form.Item
                                            name="applicableVariants"
                                            label={`Select ${optionName || 'Variants'} for Hot Deal`}
                                            rules={[{ required: true, message: 'Select at least one variant' }]}
                                            extra="The entered discount will automatically update prices for all selected variants."
                                        >
                                            <Checkbox.Group className="pcu-w-100">
                                                <div className="pcu-flex pcu-flex-column pcu-gap-12 pcu-bg-slate-light pcu-border-slate-light pcu-padding-16 pcu-radius-10">
                                                    {targetedVariants.map(v => (
                                                        <Checkbox key={v.value} value={v.value} className="pcu-font-14">
                                                            <span className="pcu-font-600">{v.value}</span>
                                                            <span className="pcu-color-muted pcu-margin-left-10">(MRP: ₹{v.mrp || product.mrp})</span>
                                                        </Checkbox>
                                                    ))}
                                                </div>
                                            </Checkbox.Group>
                                        </Form.Item>
                                    ) : (optionName && (
                                        <div className="pcu-margin-bottom-20 pcu-color-error pcu-font-13 pcu-bg-error-light pcu-padding-12 pcu-radius-8 pcu-border-error-light">
                                            No variants found for this selection.
                                        </div>
                                    ))}
                                </>
                            );
                        }}
                    </Form.Item>

                    <Form.Item noStyle shouldUpdate>
                        {({ getFieldValue }) => {
                            const pid = getFieldValue('productId');
                            const optName = getFieldValue('optionName');
                            const applicable = getFieldValue('applicableVariants') || [];
                            const discount = getFieldValue('discountPercentage');
                            const product = products.find(p => p._id === pid || p.id === pid);

                            if (!product || applicable.length <= 1 || !discount) return null;

                            // Calculate prices for preview
                            let variants = [];
                            if (optName) {
                                variants = product.options.find(o => o.name === optName)?.variants || [];
                            } else {
                                variants = product.variants || [];
                            }

                            const calcs = variants.filter(v => applicable.includes(v.value)).map(v => {
                                const mrp = v.mrp || v.price || product.mrp;
                                const dealPrice = Math.round(mrp * (1 - discount / 100));
                                return { value: v.value, mrp, dealPrice };
                            });

                            return (
                                <div className="pcu-bg-success-light pcu-border-success-light pcu-padding-12 pcu-radius-8 pcu-margin-bottom-20">
                                    <div className="pcu-font-600 pcu-font-12 pcu-color-success pcu-margin-bottom-8">Dynamic Deal Price Breakdown:</div>
                                    <div className="pcu-flex pcu-flex-wrap pcu-gap-8">
                                        {calcs.map(c => (
                                            <Tag key={c.value} color="success" className="pcu-padding-x-8 pcu-padding-y-4 pcu-radius-4">
                                                {c.value}: ₹{c.dealPrice} <span className="pcu-text-strike pcu-opacity-60 pcu-margin-left-4">₹{c.mrp}</span>
                                            </Tag>
                                        ))}
                                    </div>
                                </div>
                            );
                        }}
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="dateRange"
                                label="Duration (Start - End)"
                                rules={[{ required: true, message: 'Select duration' }]}
                            >
                                <RangePicker showTime format="YYYY-MM-DD HH:mm" className="pcu-w-100" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="discountPercentage"
                                label="Discount %"
                                rules={[{ required: true, message: 'Enter discount' }]}
                            >
                                <InputNumber
                                    min={0}
                                    max={100}
                                    formatter={value => `${value}%`}
                                    parser={value => value.replace('%', '')}
                                    className="pcu-w-100"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="dealPrice"
                                label="Deal Price (₹)"
                                rules={[{ required: true, message: 'Enter deal price' }]}
                            >
                                <InputNumber
                                    min={0}
                                    className="pcu-w-100"
                                    prefix="₹"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <div className="pcu-margin-top-20 pcu-text-right">
                        <GlobalButton action="cancel" variant="page" onClick={handleModalClose} disabled={submitting}>
                            Cancel
                        </GlobalButton>
                        <GlobalButton action="submit" loading={submitting} type="primary" htmlType="submit" className="pcu-margin-left-10">
                            {editingDeal ? "Update Deal" : "Create Deal"}
                        </GlobalButton>
                    </div>
                </Form>
            </Modal>

            {/* Deal Details Modal */}
            <Modal
                open={detailsVisible}
                onCancel={() => setDetailsVisible(false)}
                footer={null}
                width={650}
                closable={true}
                className="ss-modal"
            >
                {selectedDeal && (
                    <div className="ss-modal-body">
                        <div className="ss-modal-hero" style={{ position: 'relative' }}>

                            <div className="ss-modal-img">
                                <img
                                    src={getImageUrl(selectedDeal.product?.imageUrl) || 'https://via.placeholder.com/110?text=N/A'}
                                    alt={selectedDeal.product?.name}
                                    style={{ width: 110, height: 110, objectFit: 'cover' }}
                                />
                            </div>
                            <div className="ss-modal-info">
                                <h2 className="ss-modal-name">{selectedDeal.product?.name}</h2>
                                <div style={{ fontSize: 16, fontWeight: 700, color: '#FF8800', marginBottom: 10 }}>
                                    ₹{selectedDeal.dealPrice}
                                    {selectedDeal.product?.mrp && (
                                        <span style={{ fontSize: 13, color: '#9ca3af', textDecoration: 'line-through', marginLeft: 8, fontWeight: 500 }}>
                                            ₹{selectedDeal.product?.mrp}
                                        </span>
                                    )}
                                    <span style={{ fontSize: 13, color: '#52c41a', marginLeft: 8, fontWeight: 600 }}>
                                        ({selectedDeal.discountPercentage}% OFF)
                                    </span>
                                </div>
                                <div className="ss-modal-tags">
                                    <span className={`ss-tag ss-tag-${selectedDeal.product?.stock > 0 ? "green" : "red"}`}>
                                        {selectedDeal.product?.stock > 0 ? `In Stock (${selectedDeal.product.stock})` : "Out of Stock"}
                                    </span>
                                    <span className={`ss-tag ss-tag-${selectedDeal.isActive ? "blue" : "grey"}`}>
                                        {selectedDeal.isActive ? "Active" : "Inactive"}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="ss-modal-details">
                            <div className="ss-modal-row">
                                <div className="ss-modal-label">Duration</div>
                                <div className="ss-modal-value">
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        <div><span style={{ color: '#888', marginRight: 4 }}>Start:</span>{dayjs(selectedDeal.startDate).format('MMM D, YYYY HH:mm')}</div>
                                        <div><span style={{ color: '#888', marginRight: 4 }}>End:</span>{dayjs(selectedDeal.endDate).format('MMM D, YYYY HH:mm')}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="ss-modal-row" style={{ borderTop: '1px solid #f3f4f6', marginTop: 10 }}>
                                <div className="ss-modal-label">Deal Settings</div>
                                <div className="ss-modal-value">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <span>Live on Store:</span>
                                        <Switch
                                            checked={selectedDeal.isLive}
                                            onChange={() => toggleLive(selectedDeal)}
                                            checkedChildren="Active"
                                            unCheckedChildren="Inactive"
                                            size="small"
                                            style={{ backgroundColor: selectedDeal.isLive ? '#52c41a' : '#ff4d4f' }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="ss-modal-footer" style={{ justifyContent: 'space-between' }}>
                            <Button
                                icon={<EditOutlined />}
                                onClick={() => { handleEdit(selectedDeal); setDetailsVisible(false); }}
                                style={{ borderRadius: 8, color: '#4776E6', borderColor: '#4776E6', height: 38, fontWeight: 600 }}
                            >
                                Edit Deal
                            </Button>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <Popconfirm
                                    title="Remove this deal?"
                                    description="This action cannot be undone."
                                    onConfirm={() => { handleDelete(selectedDeal); setDetailsVisible(false); }}
                                    okText="Yes, Remove"
                                    cancelText="No"
                                >
                                    <button className="ss-modal-close-btn" style={{ background: '#fff1f0', color: '#ff4d4f', border: '1px solid #ffccc7' }}>
                                        <DeleteOutlined style={{ marginRight: '6px' }} /> Remove Deal
                                    </button>
                                </Popconfirm>
                                <button className="ss-modal-close-btn" onClick={() => setDetailsVisible(false)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default DealsManager;
